import {
  createWeb3Modal,
  defaultConfig,
  useDisconnect,
  useWeb3ModalAccount,
  useWeb3ModalProvider,
} from "@web3modal/ethers/react";
import { BrowserProvider, Contract, Interface } from "ethers";
import { PropsWithChildren, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LOCAL_STORAGE_KEY,
  THEME,
  TOAST_ID,
  WEB3_MODAL_STATUUS,
} from "../common/const";
import { WalletContext } from "../context/WalletContext";
import STAKING_ADMIN_ABI from "../common/STAKING_ADMIN_ABI.json";

export const WalletProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { walletProvider } = useWeb3ModalProvider();
  const { disconnect } = useDisconnect();

  const { address, chainId, isConnected, status } = useWeb3ModalAccount();

  const [theme, setTheme] = useState<string>(THEME.DARK);

  useEffect(() => {
    const chainInfo = {
      chainId: parseInt(process.env.REACT_APP_CHAIN_ID!),
      name: process.env.REACT_APP_CHAIN_NAME!,
      currency: process.env.REACT_APP_CHAIN_CURRENCY!,
      explorerUrl: process.env.REACT_APP_CHAIN_EXPLORER!,
      rpcUrl: process.env.REACT_APP_CHAIN_RPC!,
    };

    const metadata = {
      name: "Staking",
      description: "dApp for the escrow system",
      url: "https://upcx-staking.io",
      icons: ["https://avatars.mywebsite.com/"],
    };

    createWeb3Modal({
      ethersConfig: defaultConfig({ metadata }),
      chains: [chainInfo],
      projectId: process.env.REACT_APP_WALLET_PROJECT_ID!,
      themeVariables: {
        "--w3m-accent": "#007aff",
      },
    });
  }, []);

  useEffect(() => {
    const theme = localStorage.getItem(LOCAL_STORAGE_KEY.THEME);
    setTheme(theme ?? THEME.DARK);
  }, []);

  useEffect(() => {
    if (theme === THEME.DARK) {
      document.documentElement.className = "dark";
      document.documentElement.setAttribute("data-header-styles", "dark");
    } else {
      document.documentElement.className = "light";
      document.documentElement.setAttribute("data-header-styles", "light");
    }
  }, [theme]);

  useEffect(() => {
    if (status === WEB3_MODAL_STATUUS.RECONNECTING) {
      return;
    }

    if (status === WEB3_MODAL_STATUUS.DISCONNECTED) {
      if (location.pathname !== "/") {
        navigate("/");
        toast.warn("Please connect the wallet.", {
          theme: theme,
          toastId: TOAST_ID.ADMIN_WALLET,
        });
      }
      return;
    }

    if (chainId !== parseInt(process.env.REACT_APP_CHAIN_ID!)) {
      if (location.pathname !== "/") {
        navigate("/");
      }

      toast.warn("Please connect the correct network.", {
        theme: theme,
        toastId: TOAST_ID.CORRECT_NETWORK,
      });
      return;
    }

    getAdminAddress().then((res) => {
      if (res) {
        if (res !== address) {
          if (location.pathname !== "/") {
            navigate("/");
          }
          toast.warn("Please connect the admin wallet.", {
            theme: theme!,
            toastId: TOAST_ID.ADMIN_WALLET,
          });
        } else {
          if (location.pathname === "/") {
            navigate("/dashboard");
          }
        }
      } else {
        if (location.pathname !== "/") {
          navigate("/");
          toast.warn("Checking admin wallet has been failed.", {
            theme: theme!,
          });
        }
      }
    });
  }, [address, location.pathname, chainId, status]);

  const getAdminAddress = async () => {
    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      const stakingAdmin = new Contract(
        process.env.REACT_APP_STAKING_ADMIN_CONTRACT!,
        new Interface(STAKING_ADMIN_ABI).format(false),
        signer
      );

      const owner = await stakingAdmin.owner();
      return owner;
    } catch (err) {
      console.log(err);
    }
    return null;
  };

  const handleTheme = (theme: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEY.THEME, theme);
    setTheme(theme);
  };

  return (
    <WalletContext.Provider
      value={{
        isConnected,
        currentAddress: address,
        disconnect: disconnect,
        theme: theme,
        handleTheme: handleTheme,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

export default WalletProvider;
